<template>
    <md-card class="island-card">
        <md-card-content>
            <p class="moow-info-txt">Specify the contract fee</p>
            <p class="moow-info-txt secondary">This setting determines the booking fee the tenant will pay during the booking application process</p>
            <p class="moow-info-txt secondary">Specify the precentage (e.g {{ bookingFeePercentageUpperLimit }}%) from total deal sum and an upper fixed limit (e.g {{ bookingFeeValueUpperLimit }}&euro;).</p>
            <hr class="hr-form-el-separator"/>
            <b-row>
                <b-col cols="3" lg="3">
                    <md-field :class="{ 'md-invalid': !!bookingFeeValueError }">
                        <label>Upper fixed limit (euros)</label>
                        <span class="md-prefix">&euro;</span>
                        <md-input
                            :value="bookingFeeValue"
                            @input="bookingFeeValueChanged"
                            type="number"
                            step="1"
                            :min="bookingFeeValueLowerLimit"
                            :max="bookingFeeValueUpperLimit"
                            :disabled="requestPending"
                        ></md-input>
                        <span class="md-error" v-if="bookingFeeValueError">{{ bookingFeeValueError }}</span>
                    </md-field>
                </b-col>
                <b-col cols="3" lg="3">
                    <md-field :class="{ 'md-invalid': !!bookingFeePercentageError }">
                        <label>From deal sum (percentage)</label>
                        <span class="md-prefix">%</span>
                        <md-input
                            :value="bookingFeePercentage"
                            @input="bookingFeePercentageChanged"
                            type="number"
                            step="1"
                            :min="bookingFeePercentageLowerLimit"
                            :max="bookingFeePercentageUpperLimit"
                            :disabled="requestPending"
                        ></md-input>
                        <span class="md-error" v-if="bookingFeePercentageError">{{ bookingFeePercentageError }}</span>
                    </md-field>
                </b-col>
            </b-row>
            <b-row>
                <!--<b-col>{{accountingSaveStatus}}</b-col>-->
            </b-row>
        </md-card-content>
        <md-card-actions>
            <md-button class="md-primary play-cta-button makeup"
                       @click="saveChanges()"
                       :disabled="requestPending || !!bookingFeeValueError || !!bookingFeePercentageError"
                       :loading="requestPending"
            >Save</md-button>
        </md-card-actions>
    </md-card>
</template>

<script>
    const DEFAULT_BOOKING_FEE_VALUE_LOWER_LIMIT = 190;
    const DEFAULT_BOOKING_FEE_VALUE_UPPER_LIMIT = 400;
    const DEFAULT_BOOKING_FEE_PERCENTAGE_LOWER_LIMIT = 10;
    const DEFAULT_BOOKING_FEE_PERCENTAGE_UPPER_LIMIT = 50;

    export default {
        name: "ContractFeeCard",
        props: {
            actualBookingFeeValue: {
                type: Number,
                default: DEFAULT_BOOKING_FEE_VALUE_LOWER_LIMIT,
            },
            actualBookingFeePercentage: {
                type: Number,
                default: DEFAULT_BOOKING_FEE_PERCENTAGE_LOWER_LIMIT,
            },
            propertyId: {
                type: String,
                required: true,
            }
        },
        created() {
            this.bookingFeeValue = this.actualBookingFeeValue;
            this.bookingFeePercentage = this.actualBookingFeePercentage;
        },
        data() {
            return {
                defaultBookingFeeValueLowerLimit: DEFAULT_BOOKING_FEE_VALUE_LOWER_LIMIT,
                defaultBookingFeePercentageLowerLimit: DEFAULT_BOOKING_FEE_PERCENTAGE_LOWER_LIMIT,
                defaultBookingFeeValueUpperLimit: DEFAULT_BOOKING_FEE_VALUE_UPPER_LIMIT,
                defaultBookingFeePercentageUpperLimit: DEFAULT_BOOKING_FEE_PERCENTAGE_UPPER_LIMIT,
                bookingFeeValue: 0,
                bookingFeePercentage: 0,
                requestPending: false,
                bookingFeeValueError: '',
                bookingFeePercentageError: '',
            }
        },
        watch: {
            actualBookingFeeValue(value) {
                this.bookingFeeValue = value;
            },
            actualBookingFeePercentage(value) {
                this.bookingFeePercentage = value;
            },
        },
        computed: {
            bookingFeeValueLowerLimit() {
                const { min_moowle_rent_fee = this.defaultBookingFeeValueLowerLimit } = this.$store.pricingRules || {};

                return Number(min_moowle_rent_fee);
            },
            bookingFeePercentageLowerLimit() {
                const { min_moowle_rent_fee_percentage = this.defaultBookingFeePercentageLowerLimit } = this.$store.pricingRules || {};

                return Number(min_moowle_rent_fee_percentage);
            },
            bookingFeeValueUpperLimit() {
                const { max_moowle_rent_fee = this.defaultBookingFeeValueUpperLimit } = this.$store.pricingRules || {};

                return Number(max_moowle_rent_fee);
            },
            bookingFeePercentageUpperLimit() {
                const { moowle_rent_fee_percentage = this.defaultBookingFeePercentageUpperLimit } = this.$store.pricingRules || {};

                return Number(moowle_rent_fee_percentage);
            },
        },
        methods: {
            bookingFeeValueChanged(value) {
                const validationResult = this.validateBookingFeeValue(value);
                if (!validationResult.valid) {
                    this.bookingFeeValueError = validationResult.error;
                    return;
                }
                else {
                    this.bookingFeeValueError = '';
                }
                this.refineBookingFeeValue(value);
                this.emitChangeEvent();
            },
            bookingFeePercentageChanged(percentage) {
                const validationResult = this.validateBookingFeePercentage(percentage);
                if (!validationResult.valid) {
                    this.bookingFeePercentageError = validationResult.error;
                    return;
                }
                else {
                    this.bookingFeePercentageError = '';
                }
                this.refineBookingFeePercentage(percentage);
                this.emitChangeEvent();
            },
            validateBookingFeeValue(value) {
                if (isNaN(value)) {
                    return { valid: false, error: 'Value has to be numeric' };
                }
                const bookingFeeValue = Number(value);
                if (bookingFeeValue < this.bookingFeeValueLowerLimit) {
                    return {
                        valid: false,
                        error: `Value has to be greater than or equal to ${this.bookingFeeValueLowerLimit}`,
                    };
                }
                if (bookingFeeValue > this.bookingFeeValueUpperLimit) {
                    return {
                        valid: false,
                        error: `Value has to be less than or equal to ${this.bookingFeeValueUpperLimit}`,
                    };
                }

                return { valid: true, error: null };
            },
            validateBookingFeePercentage(percentage) {
                if (isNaN(percentage)) {
                    return { valid: false, error: 'Value has to be numeric' };
                }
                const bookingFeePercentage = Number(percentage);
                if (bookingFeePercentage < this.bookingFeePercentageLowerLimit) {
                    return {
                        valid: false,
                        error: `Value has to be greater than or equal to ${this.bookingFeePercentageLowerLimit}`,
                    };
                }
                if (bookingFeePercentage > this.bookingFeePercentageUpperLimit) {
                    return {
                        valid: false,
                        error: `Value has to be less than or equal to ${this.bookingFeePercentageUpperLimit}`,
                    };
                }

                return { valid: true, error: null };
            },
            refineBookingFeeValue(value) {
                let bookingFeeValue = Number(value);
                if (bookingFeeValue < this.bookingFeeValueLowerLimit) {
                    bookingFeeValue = this.bookingFeeValueLowerLimit;
                }
                if (bookingFeeValue > this.bookingFeeValueUpperLimit) {
                    bookingFeeValue = this.bookingFeeValueUpperLimit;
                }
                this.bookingFeeValue = bookingFeeValue;
            },
            refineBookingFeePercentage(percentage) {
                let bookingFeePercentage = Number(percentage);
                if (bookingFeePercentage < this.bookingFeePercentageLowerLimit) {
                    bookingFeePercentage = this.bookingFeePercentageLowerLimit;
                }
                if (bookingFeePercentage > this.bookingFeePercentageUpperLimit) {
                    bookingFeePercentage = this.bookingFeePercentageUpperLimit;
                }
                this.bookingFeePercentage = bookingFeePercentage;
            },
            emitChangeEvent() {
                this.$emit('change', {
                    bookingFeeValue: this.bookingFeeValue,
                    bookingFeePercentage: this.bookingFeePercentage,
                });
            },
            async saveChanges() {
                try {
                    this.requestPending = true;
                    await this.$store.dispatch('updateFees', {
                        propertyId: this.propertyId,
                        fees: {
                            bookingFeeValue: this.bookingFeeValue,
                            bookingFeePercentage: this.bookingFeePercentage,
                        },
                    });
                    this.$emit('changesSaved', {
                        bookingFeeValue: this.bookingFeeValue,
                        bookingFeePercentage: this.bookingFeePercentage,
                    });
                }
                catch (error) {
                    console.error(error);
                }
                finally {
                    this.requestPending = false;
                }
            },
        },
    }
</script>

<style scoped>

</style>


// WEBPACK FOOTER //
// src/components/ContractFeeCard.vue